// Self-hosted OTA live updates — the logic half (no React in here).
//
// Ships JS/CSS changes to installed Android apps without a Play Store release,
// using @capgo/capacitor-updater in MANUAL mode against our own server. The paid
// Capgo cloud is not involved: we host latest.json + the zips ourselves and do
// the version check here.
//
// ⚠️ WHAT CAN AND CANNOT GO OTA
// An OTA bundle replaces the WEB assets only. Anything native — a new Capacitor
// plugin, a permission, an SDK level, the app icon, versionCode — needs a real
// Play release. Shipping web code that calls a plugin the installed APK doesn't
// have will simply fail at runtime on every updated device.
//
// The flow each launch (and each resume):
//   1. notifyAppReady()  — MANDATORY. After applying a bundle the plugin arms a
//      ~10 s watchdog; if this call doesn't land it assumes the bundle crashed
//      and rolls back. Miss it and every OTA silently reverts.
//   2. GET <base>/latest.json via CapacitorHttp (native — no CORS on the server).
//   3. If its version is strictly greater than the floor, download() then
//      next() — applied on the NEXT cold start, never mid-session.
//
// The floor is max(running bundle version, native versionName). Flooring at the
// APK version is what makes it impossible for a stale or wrong latest.json to
// downgrade someone below what Play installed.
//
// THE OTHER DIRECTION — `minNative` in the manifest. The floor above stops a
// bundle that is too OLD. Nothing stopped a bundle that is too NEW: web code
// calling a plugin the installed APK does not contain, which fails at runtime on
// every phone that takes it, with no way back except another OTA. A bundle can
// now declare the minimum APK it needs, and phones below that simply do not take
// it — they stay on what works until Play catches up.
//
// It is a DECLARATION, not something derivable: whether a bundle truly requires
// a plugin depends on whether it degrades without it. A release that shows
// "update the app" where a scan would be is safe on an older APK; one that
// crashes is not. So the release engineer sets it (package.json "otaMinNative")
// and this enforces it.
//
// Everything fails open: any network, parse or plugin error is swallowed. An OTA
// problem must never stop the app from starting.

import { Capacitor, CapacitorHttp } from '@capacitor/core';

export const OTA_BASE = (
  process.env.REACT_APP_OTA_URL || ''
).replace(/\/$/, '');

export function otaSupported() {
  try {
    return Capacitor.getPlatform() === 'android' && !!OTA_BASE;
  } catch (e) {
    return false;
  }
}

// "1.5.4" > "1.5.3". Numeric segments only — no pre-release tags.
export function semverGt(a, b) {
  const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < 3; i += 1) {
    if ((pa[i] || 0) > (pb[i] || 0)) return true;
    if ((pa[i] || 0) < (pb[i] || 0)) return false;
  }
  return false;
}

// Why the last check did nothing. `null` means "no update", which is also what
// every failure returned — see the note on fetchManifest.
export const OTA_REASON = {
  UP_TO_DATE: 'up-to-date',
  UNSUPPORTED: 'not-a-native-android-build',
  UNREACHABLE: 'manifest-unreachable',
  NOT_JSON: 'manifest-is-not-json',
  MALFORMED: 'manifest-missing-version-or-url',
  NEEDS_NEWER_APP: 'needs-a-newer-app',
  STAGED: 'staged',
  ERROR: 'error',
};

// The last outcome, for the UI to show. A user cannot read a console line, and
// this whole subsystem's failure mode is looking exactly like success.
let lastResult = { reason: null, at: null, detail: '' };
export const lastOtaResult = () => lastResult;
const record = (reason, detail = '', extra = {}) => {
  lastResult = {
    reason, detail, at: Date.now(), ...extra,
  };
  return lastResult;
};

/**
 * The manifest, or a typed failure.
 *
 * ⚠️ THIS FUNCTION HID A THREE-WEEK OUTAGE, and the way it did it is worth
 * keeping in front of whoever edits it next. It used to `return null` on all
 * four failure paths, and `null` is also the legitimate "nothing new" answer.
 *
 * The OTA host answered every request with its website's `index.html` — HTTP
 * **200**, `Content-Type: text/html`, 546 bytes — because the manifest had never
 * been published to a path that vhost serves. So:
 *
 *     status 200            -> passes the status check
 *     JSON.parse('<!doct…') -> throws, caught, `return null`
 *     caller                -> "no update available"
 *
 * The `console.warn` at the bottom of `checkForUpdate` sits in the OUTER catch
 * and was never reached. Eight bundles were packed over three weeks and not one
 * phone ever saw a manifest, with no diagnostic anywhere in the system.
 *
 * A 200 carrying HTML is the single most likely way for this to break, because
 * it is what every SPA host does with a path it cannot resolve — so it gets its
 * own reason code and says the diagnosis out loud.
 */
async function fetchManifest() {
  // CapacitorHttp is the NATIVE http stack, so it bypasses the WebView's CORS
  // rules — the OTA host needs no Access-Control-Allow-Origin header. The
  // cache-buster stops a CDN or proxy pinning an old manifest.
  const res = await CapacitorHttp.get({
    url: `${OTA_BASE}/latest.json?t=${Date.now()}`,
    headers: { 'Cache-Control': 'no-cache' },
  });
  if (res.status !== 200) {
    // eslint-disable-next-line no-console
    console.warn(`[OTA] ${OTA_BASE}/latest.json -> HTTP ${res.status}`);
    return { error: OTA_REASON.UNREACHABLE, detail: `HTTP ${res.status}` };
  }

  let manifest = res.data;
  if (typeof manifest === 'string') {
    try {
      manifest = JSON.parse(manifest);
    } catch (e) {
      const head = manifest.slice(0, 80).replace(/\s+/g, ' ');
      // eslint-disable-next-line no-console
      console.warn(
        `[OTA] ${OTA_BASE}/latest.json returned 200 but is NOT JSON — the OTA host `
        + `is serving a web page where the manifest should be. Body starts: ${head}`,
      );
      return { error: OTA_REASON.NOT_JSON, detail: head };
    }
  }
  if (!manifest || !manifest.version || !manifest.url) {
    // eslint-disable-next-line no-console
    console.warn('[OTA] manifest parsed but has no version/url:', JSON.stringify(manifest));
    return { error: OTA_REASON.MALFORMED, detail: JSON.stringify(manifest || null) };
  }
  return { manifest };
}

// Check once and, if there is something newer, stage it for the next restart.
// Returns the staged version string, or null.
export async function checkForUpdate(CapacitorUpdater) {
  if (!otaSupported()) {
    record(OTA_REASON.UNSUPPORTED, OTA_BASE ? 'not android' : 'REACT_APP_OTA_URL is empty');
    return null;
  }
  try {
    const { manifest, error, detail } = await fetchManifest();
    if (!manifest) { record(error, detail); return null; }

    const builtin = await CapacitorUpdater.getBuiltinVersion()
      .catch(() => ({ version: '0.0.0' }));
    const cur = await CapacitorUpdater.current();
    const curRaw = cur && cur.bundle && cur.bundle.version;
    // current() reports the literal 'builtin' for the shipped bundle.
    const curVersion = (curRaw && curRaw !== 'builtin') ? curRaw : builtin.version;
    const floor = semverGt(curVersion, builtin.version) ? curVersion : builtin.version;

    if (!semverGt(manifest.version, floor)) {
      record(OTA_REASON.UP_TO_DATE, `server ${manifest.version}, running ${floor}`, {
        serverVersion: manifest.version,
        runningVersion: floor,
      });
      return null;
    }

    // Too new for this APK's native side — skip, and say so loudly enough to be
    // findable, because from the user's side this looks like OTA has stopped.
    // builtin.version is the APK's versionName; the running bundle is
    // irrelevant here, since a live update never changes the native code.
    if (manifest.minNative && semverGt(manifest.minNative, builtin.version)) {
      // eslint-disable-next-line no-console
      console.log(
        `[OTA] ${manifest.version} needs app ${manifest.minNative}, `
        + `this is ${builtin.version} — waiting for a Play update`,
      );
      record(
        OTA_REASON.NEEDS_NEWER_APP,
        `${manifest.version} needs app ${manifest.minNative}, this is ${builtin.version}`,
        { serverVersion: manifest.version, runningVersion: builtin.version },
      );
      return null;
    }

    const bundle = await CapacitorUpdater.download({
      url: manifest.url,
      version: manifest.version,
      ...(manifest.checksum ? { checksum: manifest.checksum } : {}),
    });
    // next() = apply on next cold start. set() would reload the webview
    // immediately and yank the screen out from under whatever the user is doing.
    await CapacitorUpdater.next({ id: bundle.id });
    // eslint-disable-next-line no-console
    console.log(`[OTA] downloaded ${manifest.version}; applies on next restart`);
    record(OTA_REASON.STAGED, `${manifest.version} applies on next restart`, {
      serverVersion: manifest.version,
    });
    return manifest.version;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[OTA] update check skipped:', (err && err.message) || err);
    record(OTA_REASON.ERROR, (err && err.message) || String(err));
    return null;
  }
}

// Load the native plugin. Dynamic import so a web/desktop build never tries to
// resolve the native module.
//
// ⚠️ RETURNS A WRAPPER — `const { updater } = await loadUpdater()` — NEVER THE
// PLUGIN ITSELF. A Capacitor plugin is a Proxy that answers every property with
// a native-method wrapper, `then` included (@capacitor/core special-cases only
// $typeof and toJSON). Returned from an async function it is treated as a
// thenable, a native "then" is called that never answers, and the await hangs
// forever — so notifyAppReady() and checkForUpdate() never ran. Found
// 2026-09-29 through the same bug in core/auth/biometricGate.
export async function loadUpdater() {
  const mod = await import('@capgo/capacitor-updater');
  return { updater: mod.CapacitorUpdater };
}
